import axios from "axios";
import AuthService from "../../../services/auth.service";
import HostService from "../../../services/host.service";

/**
 * Servicio para el endpoint /traspasos
 * API documentada en ../api/docs/api/traspasos.md
 *
 * La API devuelve responses en formato: { data: ..., message: "..." }
 * Todos los campos están en camelCase
 */
class TraspasosService {

    /**
     * GET /traspasos - lista traspasos con filtros opcionales
     *
     * @param {Object} params - Filtros opcionales
     * @param {string} params.almacenOrigen - Código de almacén origen
     * @param {string} params.almacenDestino - Código de almacén destino
     * @param {string} params.estado - PENDIENTE | TRASPASADO
     * @param {string} params.fechaDesde - Fecha desde (YYYY-MM-DD o ISO 8601)
     * @param {string} params.fechaHasta - Fecha hasta (YYYY-MM-DD o ISO 8601)
     * @returns {Promise<Array>} Array de traspasos con totalLineas y totalUnidades calculados
     */
    static async list({ almacenOrigen, almacenDestino, estado, fechaDesde, fechaHasta } = {}) {
        const params = {};

        if (almacenOrigen) params.almacenOrigen = almacenOrigen;
        if (almacenDestino) params.almacenDestino = almacenDestino;
        if (estado) params.estado = estado;
        if (fechaDesde) params.fechaDesde = fechaDesde;
        if (fechaHasta) params.fechaHasta = fechaHasta;

        return axios
            .get(
                `${HostService.getHost()}traspasos`,
                {
                    params,
                    ...AuthService.setToken(),
                }
            )
            .then((response) => {
                // La API devuelve { data: [...], message: "..." }
                const items = response.data?.data ?? [];

                // Enriquecemos con totalLineas y totalUnidades
                return Array.isArray(items) ? items.map((t) => {
                    const lineas = t.lineas ?? [];
                    return {
                        ...t,
                        totalLineas: lineas.length,
                        totalUnidades: lineas.reduce((acc, l) => acc + (Number(l.unidades) || 0), 0),
                    };
                }) : [];
            })
            .catch((err) => {
                console.error('Error al listar traspasos:', err);
                throw err;
            });
    }

    /**
     * GET /traspasos/count - cuenta traspasos que cumplen los filtros sin cargar los datos
     *
     * @param {Object} params - Filtros opcionales
     * @param {string} params.almacenOrigen - Código de almacén origen
     * @param {string} params.almacenDestino - Código de almacén destino
     * @param {string} params.estado - PENDIENTE | TRASPASADO
     * @param {string} params.fechaDesde - Fecha desde (YYYY-MM-DD o ISO 8601)
     * @param {string} params.fechaHasta - Fecha hasta (YYYY-MM-DD o ISO 8601)
     * @returns {Promise<number>} Número de traspasos que cumplen los filtros
     */
    static async count({ almacenOrigen, almacenDestino, estado, fechaDesde, fechaHasta } = {}) {
        const params = {};

        if (almacenOrigen) params.almacenOrigen = almacenOrigen;
        if (almacenDestino) params.almacenDestino = almacenDestino;
        if (estado) params.estado = estado;
        if (fechaDesde) params.fechaDesde = fechaDesde;
        if (fechaHasta) params.fechaHasta = fechaHasta;

        return axios
            .get(
                `${HostService.getHost()}traspasos/count`,
                {
                    params,
                    ...AuthService.setToken(),
                }
            )
            .then((response) => {
                // La API devuelve { data: { count }, message: "..." }
                return Number(response.data?.data?.count) || 0;
            })
            .catch((err) => {
                console.error('Error al contar traspasos:', err);
                throw err;
            });
    }

    /**
     * GET /traspasos/:id - obtiene un traspaso por ID
     *
     * @param {number} id - ID del traspaso
     * @returns {Promise<Object>} Traspaso con sus líneas incluidas
     */
    static async getById(id) {
        return axios
            .get(
                `${HostService.getHost()}traspasos/${encodeURIComponent(id)}`,
                AuthService.setToken()
            )
            .then((response) => {
                // La API devuelve { data: {...}, message: "..." }
                const traspaso = response.data?.data ?? response.data;

                // Enriquecemos con totalLineas y totalUnidades
                const lineas = traspaso.lineas ?? [];
                return {
                    ...traspaso,
                    totalLineas: lineas.length,
                    totalUnidades: lineas.reduce((acc, l) => acc + (Number(l.unidades) || 0), 0),
                };
            })
            .catch((err) => {
                console.error('Error al obtener traspaso:', err);
                throw err;
            });
    }

    /**
     * POST /traspasos - crea un nuevo traspaso
     *
     * @param {Object} payload - Datos del traspaso
     * @param {string} payload.almacenOrigen - Código del almacén origen (requerido)
     * @param {string} payload.almacenDestino - Código del almacén destino (requerido)
     * @param {number} payload.empleadoOrigenId - ID del empleado origen (requerido)
     * @param {number} payload.empleadoDestinoId - ID del empleado destino (opcional)
     * @param {string} payload.observaciones - Observaciones (opcional)
     * @param {Array} payload.lineas - Array de líneas (requerido, mínimo 1)
     * @param {string} payload.lineas[].codigoArticulo - Código del artículo (requerido)
     * @param {number} payload.lineas[].unidades - Unidades a traspasar (requerido, > 0)
     * @returns {Promise<Object>} Traspaso creado con estado PENDIENTE
     */
    static async create(payload) {
        return axios
            .post(
                `${HostService.getHost()}traspasos`,
                payload,
                AuthService.setToken()
            )
            .then((response) => {
                // La API devuelve { data: {...}, message: "..." }
                return response.data?.data ?? response.data;
            })
            .catch((err) => {
                console.error('Error al crear traspaso:', err);
                throw err;
            });
    }

    /**
     * PUT /traspasos/:id - actualiza un traspaso existente
     * Solo se pueden actualizar traspasos en estado PENDIENTE
     *
     * @param {number} id - ID del traspaso
     * @param {Object} payload - Campos a actualizar (todos opcionales)
     * @returns {Promise<Object>} Traspaso actualizado
     */
    static async update(id, payload) {
        return axios
            .put(
                `${HostService.getHost()}traspasos/${encodeURIComponent(id)}`,
                payload,
                AuthService.setToken()
            )
            .then((response) => {
                // La API devuelve { data: {...}, message: "..." }
                return response.data?.data ?? response.data;
            })
            .catch((err) => {
                console.error('Error al actualizar traspaso:', err);
                throw err;
            });
    }

    /**
     * DELETE /traspasos/:id - elimina un traspaso
     * Solo se pueden eliminar traspasos en estado PENDIENTE
     *
     * @param {number} id - ID del traspaso
     * @returns {Promise<Object>} Mensaje de confirmación
     */
    static async delete(id) {
        return axios
            .delete(
                `${HostService.getHost()}traspasos/${encodeURIComponent(id)}`,
                AuthService.setToken()
            )
            .then((response) => response.data)
            .catch((err) => {
                console.error('Error al eliminar traspaso:', err);
                throw err;
            });
    }
}

export default TraspasosService;
